import React, { type FC, useContext, useEffect, useState } from "react";
import { Box, Button, CloseButton, Flex, Heading, Input, Modal, ModalContent, ModalOverlay, SimpleGrid, useDisclosure, useMediaQuery } from "~/foundation/Chakra";
import { createSearchParams, useNavigate } from "react-router";
import { Icon } from "~/foundation/Components/Icon";
import { useSitecoreContext } from "~/foundation/Jss";
import { ContentWrapper } from "~/foundation/Components/ContentWrapper";
import { Ribbon } from "~/foundation/Components/Ribbon";
import closeBlueIcon from "~/foundation/Assets/svg/icons/Close-blue.svg";
import { useTranslation } from "~/foundation/Dictionary";
import { type NavigationDictionary } from "../dictionary";
import { MotionBox } from "~/foundation/Framer/MotionBox";
import { AnimatePresence } from "framer-motion";
import { pushDataLayer } from "~/foundation/Tracking/GoogleTagManager";
import { ColorThemeContext } from "~/foundation/Theme/ColorThemeContext";
import { type SearchSuggestionModel } from "~/foundation/SitecoreContent/generated-types";
import { AliaSearchContext } from "~/foundation/Search/AliaSearchContext";
import { breakpoints } from "~/foundation/Theme/variables/breakpoints";
import aliaCurveGreyOverlay from "~/foundation/Assets/svg/graphics/alia-curve-inverted-grey-overlay.svg";
type SearchProps = {
	mobileView: boolean
}

export const Search: FC<SearchProps> = ({ mobileView }) => {
	const { sitecoreContext } = useSitecoreContext();
	//const isRtl = sitecoreContext.custom.settings.isRtl;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const navigate = useNavigate();
	const [t] = useTranslation<NavigationDictionary>();
	const [isOpenMobile, setIsOpenMobile] = useState<boolean>(false);
	const inputRef = React.useRef<HTMLInputElement>(null);
	const [theme] = useContext(ColorThemeContext);
	const [randomizedSuggestions, setRandomizedSuggestions] = useState<SearchSuggestionModel[]>([]);
	const [aliaSearchContextValue, setAliaSearchContextValue] = useContext(AliaSearchContext);
	const [isSmBreakpointOrAbove] = useMediaQuery(`(min-width: ${breakpoints.sm})`);
	const [isMdBreakpointOrAbove] = useMediaQuery(`(min-width: ${breakpoints.md})`);
	const [isLgBreakpointOrAbove] = useMediaQuery(`(min-width: ${breakpoints.lg})`);
	const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState("");

	const aliaEnabled = sitecoreContext.custom.settings.aliaSettings?.enabledForSearch || false;
	const searchSuggestions = sitecoreContext.custom.settings.searchSuggestions || [];
	const maxSuggestions = 8;

	useEffect(() => {
		const randomized = [...searchSuggestions].sort(() => Math.random() - 0.5).slice(0, maxSuggestions);
		setRandomizedSuggestions(randomized);
	}, []);

	useEffect(() => {
		if (isOpenMobile) {
			inputRef.current?.focus();
		}
	}, [isOpenMobile]);

	const handleQuerySuggestion = (suggestion: SearchSuggestionModel) => {
		setAliaSearchContextValue({ ...aliaSearchContextValue, expandAlia: true });

		navigate({
			pathname: sitecoreContext.custom.settings.searchResultPageUrl,
			...(suggestion.query && {
				search: createSearchParams({
					query: suggestion.query,
				}).toString()
			})
		});

		onClose(); // ensure the modal closes, e.g. if we aren't redirected
	}

	const RandomizedSuggestions = () => {
		if (!randomizedSuggestions || !randomizedSuggestions.length) {
			return null;
		}

		return (
			<Flex
				flexDirection="column"
				gap={8}
				mt={8}
				px={[10, 20, null, null]}
				py={[10, 20, null, null]}
				rounded="lg"
				bg="primary.darkBlue"
				color="primary.white"
				position="relative"
				sx={{
					"&::after": {
						content: '""',
						position: "absolute",
						top: "1em",
						left: isMdBreakpointOrAbove ? "-8.8px" : "-5.5px",
						background: `url("${aliaCurveGreyOverlay}")`,
						backgroundRepeat: "no-repeat",
						backgroundSize: "contain",
						width: isMdBreakpointOrAbove ? "34.5px" : "30px",
						height: isMdBreakpointOrAbove ? "58.5px" : "40px",
					}
				}}
			>
				<Heading as="h3" fontSize="body" fontWeight="bold" color="primary.white">{t("navigation.search.suggestionsLabel")}</Heading>
				<SimpleGrid columns={[1, 2, null, 4, null]} gap={6}>
					{randomizedSuggestions.map((suggestion, i) => (
						suggestion.query && (
							<Box
								key={i}
								pb="1rem"
								borderBottom="1px solid"
								borderColor="rgba(255, 255, 255, 0.2)"
								{...(i < (isLgBreakpointOrAbove ? 4 : (isSmBreakpointOrAbove ? 2 : 1)) && {
									borderTop: "1px solid",
									borderTopColor: "rgba(255, 255, 255, 0.2)",
									pt: "1rem"
								})}
							>
								<Button
									onMouseDown={(e) => {
										e.preventDefault();
										handleQuerySuggestion(suggestion);
									}}
									type="button"
									aria-label={t("navigation.search.linkLabel") + " " + (suggestion.label || suggestion.query)}
									variant="unstyled"
									h="fit-content"
									fontSize="body"
								>
									{suggestion.label || suggestion.query}
								</Button>
							</Box>
						)
					))}
				</SimpleGrid>
			</Flex>
		)
	}

	return (
		<>
			{(sitecoreContext.custom.settings.searchResultPageUrl && sitecoreContext.custom.settings.searchResultPageUrl !== "") &&
				<Box zIndex={20}>
					{mobileView ?
						<Flex>
							{!isOpenMobile &&
								<Button
									onClick={() => setIsOpenMobile(true)}
									border="2px solid transparent"
									title={t("navigation.mainNavigation.searchIconTitle")}
									aria-label={t("navigation.mainNavigation.searchIconTitle")}
								>
									<Icon
										variant={aliaEnabled ? "SearchAlia" : "Search"}
										w="icons.lg"
										ms="0"
										sx={{
											"path": {
												"fill": "primary.neutralGrey"
											}
										}}
										aria-hidden="true"
									/>
								</Button>
							}
							<AnimatePresence>
								{isOpenMobile &&
									<MotionBox
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={{ duration: 0.2 }}
									>
										<Flex
											bg="gradients.mobileNavigation"
											flexDirection="column"
											flexWrap="wrap"
											alignItems="center"
											alignContent="flex-start"
											position="absolute"
											top="0"
											left="0"
											h="100vh"
											w="100%"
											px="var(--chakra-sizes-pageMarginSM)"
											pt="1.3125rem"
											data-tracking-type="visibility"
											data-tracking-id="search_results"
											data-tracking-data={JSON.stringify({ id: "popup" })}
										>
											<Flex
												flexDirection="row"
												flexWrap="wrap"
												alignItems="center"
												alignContent="flex-start"
												justifyContent="space-between"
												w="100%"
											>
												<Box
													position="relative"
													flexBasis="75%"
													maxW="75%"
													borderRadius="2rem"
													p={aliaEnabled ? "2px" : "0"}
													background={aliaEnabled ? "linear-gradient(to right, var(--chakra-colors-primary-aramcoGreen) 60%, var(--chakra-colors-primary-aramcoBlue) 100%)" : "transparent"}
												>
													<Flex
														as="form"
														action=""
														w="100%"
														border={aliaEnabled ? "none" : "2px solid"}
														borderColor="primary.neutralGrey"
														borderRadius="2rem"
														bg="gradients.mobileNavigation"
														onSubmit={(e) => {
															e.preventDefault();

															pushDataLayer(() => ({
																event: "GAevent",
																event_type: "click",
																event_name: "search_results",
																type: "search",
																text: searchQuery,
																group: "All",
																id: "popup"
															}));

															navigate({
																pathname: sitecoreContext.custom.settings.searchResultPageUrl,
																...(searchQuery && {
																	search: createSearchParams({
																		query: searchQuery
																	}).toString()
																})
															});
														}}
													>
														<Button
															type="submit"
															title={t("navigation.mainNavigation.searchIconTitle")}
															aria-label={t("navigation.mainNavigation.searchIconTitle")}
															onClick={() => setIsOpenMobile(true)}
														>
															<Icon
																variant={aliaEnabled ? "SearchAlia" : "Search"}
																w="icons.lg"
																ms="3"
																sx={{
																	"path": {
																		"fill": "primary.neutralGrey"
																	}
																}}
																aria-hidden="true"
															/>
														</Button>
														<Input
															name="query"
															ref={inputRef}
															type="search"
															placeholder={t("navigation.mainNavigation.searchPlaceHolder")}
															backgroundColor="transparent"
															border="none"
															fontSize="xs"
															w="100%"
															p=".75rem"
															color="primary.white"
															_placeholder={{ color: "primary.aramcoGrey" }}
															_focus={{ "border": "none", "boxShadow": "none" }}
															borderRadius="2rem"
															sx={{
																"&::-webkit-search-cancel-button": {
																	background: `url("${closeBlueIcon}")`,
																	backgroundSize: "contain",
																	appearance: "none",
																	height: "1rem",
																	width: "1rem",
																	cursor: "pointer",
																},
																"&:hover, &:focus, &:focus-within, &:focus-visible": {
																	outline: "none",
																	border: "none"
																},
																"&::placeholder": {
																	color: theme.colors.tags.color
																}
															}}
															onChange={(e) => setSearchQuery(e.target.value)}
														/>
													</Flex>
												</Box>
												<CloseButton
													onClick={() => setIsOpenMobile(false)}
													position="relative"
													color="primary.aramcoLinkBlue"
												/>
											</Flex>
											<RandomizedSuggestions />
										</Flex>
									</MotionBox>
								}
							</AnimatePresence>
						</Flex>
						:
						<>
							<Box
								as="button"
								onClick={onOpen}
								title={t("navigation.mainNavigation.searchIconTitle")}
								aria-label={t("navigation.mainNavigation.searchIconTitle")}
							>
								<Icon
									variant={aliaEnabled ? "SearchAlia" : "Search"}
									w="icons.lg"
									sx={{
										"path": {
											transitionDuration: ".25s",
											transitionTimingFunction: "aramcoCubic",
											transitionProperty: "fill",
											"fill": isOpen ? "primary.aramcoLinkBlue" : "primary.neutralGrey"
										},
										"&:hover": {
											"path": {
												"fill": "primary.aramcoLinkBlue"
											}
										}
									}}
									aria-hidden="true"
								/>
							</Box>

							<Modal
								isCentered
								onClose={onClose}
								isOpen={isOpen}
								motionPreset="slideInBottom"
								closeOnOverlayClick={true}
								initialFocusRef={inputRef as React.RefObject<HTMLInputElement>}
							>
								<ModalOverlay bg="overlay.darkGreyAlpha" />
								<ModalContent zIndex="20" top="var(--chakra-sizes-headerHeightLG)" maxH="6.25rem" bg="transparent" boxShadow="none">
									<Ribbon
										as="div"
										className="search-modal"
										data-tracking-type="visibility"
										data-tracking-id="search_results"
										data-tracking-data={JSON.stringify({ id: "popup" })}
										bgColor="transparent"
									>
										<ContentWrapper py={[0, 0, 0, 0, 0]}>
											<Flex bg="primary.white" px={[2, null, null, 3]} py={2} borderRadius="3.25rem" alignItems="center" gap={6}>
												<Box
													position="relative"
													flex="1"
													borderRadius="3.25rem"
													p={aliaEnabled && isInputFocused ? "2px" : "0"}
													background={aliaEnabled && isInputFocused ? "linear-gradient(to right, var(--chakra-colors-primary-aramcoGreen) 60%, var(--chakra-colors-primary-aramcoBlue)) 100%" : "transparent"}
												>
													<Flex
														as="form"
														w="100%"
														borderRadius="3.25rem"
														border={aliaEnabled && isInputFocused ? "none" : "2px solid"}
														borderColor={isInputFocused ? "primary.aramcoLinkBlue" : "primary.white"}
														bg="primary.white"
														alignItems="center"
														px="0.5rem"
														onSubmit={(e) => {
															e.preventDefault();
															navigate({
																pathname: sitecoreContext.custom.settings.searchResultPageUrl,
																...(searchQuery && {
																	search: createSearchParams({
																		query: searchQuery
																	}).toString()
																})
															});

															onClose();
														}}
													>
														<Box flex="1" position="relative">
															<Box
																position="absolute"
																left="1.5rem"
																top="50%"
																transform="translateY(-50%)"
																zIndex={1}
																pointerEvents="none"
																display="flex"
															>
																<Icon
																	variant={aliaEnabled ? "SearchAlia" : "Search"}
																	w="icons.lg"
																	sx={{
																		"path": {
																			"fill": "primary.aramcoLinkBlue"
																		}
																	}}
																	aria-hidden="true"
																/>
															</Box>
															<Input
																ref={inputRef}
																type="search"
																placeholder={t("navigation.mainNavigation.searchPlaceHolder")}
																pl="3.875rem"
																pr="1rem"
																py="1.5rem"
																border="none"
																borderRadius="3.25rem"
																fontSize="body"
																color="primary.text"
																_placeholder={{ color: "primary.neutralGrey" }}
																_focus={{
																	outline: "none",
																	boxShadow: "none",
																	border: "none"
																}}
																sx={{
																	"&::-webkit-search-cancel-button": {
																		display: "none"
																	}
																}}
																onChange={(e) => setSearchQuery(e.target.value)}
																onFocus={() => setIsInputFocused(true)}
																onBlur={() => setIsInputFocused(false)}
															/>
														</Box>
													</Flex>
												</Box>
												<CloseButton
													onClick={onClose}
													w="2.8125rem"
													h="2.8125rem"
													border="2px solid"
													borderColor="primary.aramcoLinkBlue"
													borderRadius="50%"
													bg="transparent"
													color="primary.aramcoLinkBlue"
													flexShrink={0}
													transitionDuration=".25s"
													transitionProperty="transform"
													transitionTimingFunction="aramcoCubic"
													_hover={{
														bg: "transparent",
														transform: [null, null, null, "rotate(45deg)"]
													}}
													sx={{
														"svg": {
															w: [".75rem", null, null, "1rem"],
															h: [".75rem", null, null, "1rem"]
														}
													}}
												/>
											</Flex>
											<RandomizedSuggestions />
										</ContentWrapper>
									</Ribbon>
								</ModalContent>
							</Modal>
						</>
					}
				</Box>
			}
		</>
	)
}
